import { enrichDemoSeed, demoExtras, recordDemoOperation, demoDelay, scheduleDemoReply, type SimulationState } from "./demoSimulation";
import type { Vehicle } from "../types";

export const DEMO_API_BASE = "demo://precision-ev";
export const DEMO_STATE_KEY = "precisionEV.demo.backend.v7";
export const DEMO_VEHICLES_KEY = "precisionEV.demo.vehicles.v5";
export const DEMO_SETTINGS_KEY = "precisionEV.demo.settings.v2";

export type DemoState = SimulationState & {
  profile: Record<string, unknown>;
  vehicles: Vehicle[];
  assistance: Record<string, Record<string, unknown>>;
};

let volatileState: DemoState | null = null;

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function shiftedIso(days: number, hour = 10, minute = 0) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
}

function nextBusinessAppointment() {
  const date = new Date();
  date.setDate(date.getDate() + 4);
  while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
  date.setHours(9, 30, 0, 0);
  return date.toISOString();
}

function localDemoSlot(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function defaultPreApprovedSlots() {
  const result: string[] = [];
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  let businessIndex = 0;
  while (businessIndex < 15) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() === 0 || date.getDay() === 6) continue;
    if (businessIndex % 5 !== 3) {
      const times = businessIndex % 2 === 0 ? ["08:00", "08:30", "09:30", "10:00", "13:30", "14:00", "15:30"] : ["07:30", "09:00", "10:30", "11:00", "14:30", "16:00", "16:30"];
      for (const time of times) result.push(`${localDemoSlot(date).slice(0, 10)}T${time}`);
    }
    businessIndex += 1;
  }
  result.push(localDemoSlot(nextBusinessAppointment()));
  return Array.from(new Set(result)).sort();
}

function createDemoDossier(request: string, termsAcceptedAt = new Date().toISOString()) {
  return {
    dossierNo: "DEMO-2401",
    demoStage: "request_received",
    request,
    diagnosis: "",
    deliveryEstimate: "A oficina irá indicar uma previsão após o diagnóstico.",
    initialDiagnostic: {
      id: "demo-initial-diagnostic",
      status: "not_requested",
      amount: 0,
      amountIncludesVat: true,
      title: "Diagnóstico inicial da vibração",
      description: "Localização da origem da vibração, leitura eletrónica e inspeção visual dos componentes acessíveis.",
      items: [
        { description: "Leitura de avarias e valores reais", total: 54.05 },
        { description: "Inspeção visual do chassis e suspensão", total: 76.0 },
        { description: "Ensaio dinâmico e relatório técnico", total: 54.8 },
      ],
    },
    appointment: {
      status: "none",
      scheduledAt: "",
      preApprovedSlots: defaultPreApprovedSlots(),
      rentalCarRequested: null,
      rentalCar: { vehicle: "SEAT Ibiza ou equivalente", dailyRate: 60, includedKmPerDay: 100, currency: "CHF", estimatedDays: 1 },
    },
    dropoff: {
      method: "",
      afterHoursRequest: { status: "none" },
      locker: { available: true, number: "3", code: "" },
    },
    quotes: [],
    diagnosticApprovals: [],
    changeRequests: [],
    payment: {
      amount: 0,
      status: "pending",
      methods: { account_card: true, paypal: true, google_pay: true, apple_pay: true, twint: true, local: true },
      customerChoice: {},
    },
    pickup: { authorizedAt: null, method: "", locker: { available: true, number: "4", code: "" } },
    attachments: [
      { id: "demo-terms-acceptance", name: "Aceitacao_cliente_DEMO.pdf", caption: "Pedido e termos aceites", createdAt: termsAcceptedAt },
    ],
    timeline: [
      { id: "demo-event-request", title: "Pedido de assistência enviado", body: request, createdAt: new Date().toISOString() },
      { id: "demo-event-terms", title: "Pedido e termos aceites", body: "Aceitação digital assinada pelo cliente.", createdAt: termsAcceptedAt },
    ],
  };
}

function createSeed(): DemoState {
  const vehicleId = "demo-vehicle-precision-01";
  const vehicle: Vehicle = {
    id: vehicleId,
    nickname: "Demo EV",
    name: "Tesla Model 3",
    variant: "Long Range AWD",
    year: 2025,
    firstRegistration: "2025-03",
    vin: "DEMOEV0000002401",
    registration: "BL 240 001",
    mileage: 28740,
    soh: 94,
    delta: 4,
    soc: 72,
    estimatedRange: 412,
    batteryCapacity: 75,
    consumption: 16.8,
    primary: true,
    precisionCertified: true,
    workshopSource: "DOSSIER",
    workshopActive: false,
    workshopStatus: "Sem serviço ativo",
    workshopProgress: 0,
    workshopActivity: "Pode iniciar um pedido na área de Assistência.",
    workshopUpdatedAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    tvpfSections: [],
    tvpfHighlights: [],
    events: [
      { id: "demo-history-1", type: "maintenance", title: "Inspeção anual Precision", value: "Concluída", date: shiftedIso(-180).slice(0, 10), source: "PRECISION_VERIFIED", odometer: 22410, workshop: "Precision Autotechnik", verifiedByPrecision: true, serviceType: "performed" },
      { id: "demo-history-2", type: "battery", title: "EV & Battery Check", value: "SOH 94% · Delta 4 mV", date: shiftedIso(-45).slice(0, 10), source: "PRECISION_VERIFIED", odometer: 27480, workshop: "Precision Autotechnik", verifiedByPrecision: true, serviceType: "checked" },
      { id: "demo-history-3", type: "charging", title: "Carregamento rápido", value: "48.2 kWh · CHF 24.10", date: shiftedIso(-8).slice(0, 10), source: "USER_REPORTED", amount: 24.1, odometer: 28510 },
      { id: "demo-history-4", type: "document", title: "Relatório de bateria", value: "PDF", date: shiftedIso(-45).slice(0, 10), source: "PRECISION_VERIFIED", workshop: "Precision Autotechnik", verifiedByPrecision: true, documents: [{ name: "EV_Battery_Check_DEMO.pdf", type: "application/pdf" }] },
      { id: "demo-history-5", type: "maintenance", title: "Filtro do habitáculo", value: "Próxima manutenção", date: shiftedIso(-20).slice(0, 10), source: "SYSTEM_CALCULATED", nextDueDate: shiftedIso(35).slice(0, 10), nextDueMileage: 32000 },
    ],
    batteryMeasurements: [
      { id: "demo-battery-1", date: shiftedIso(-200).slice(0, 10), soh: 96, delta: 3, mileage: 19520, source: "PRECISION_VERIFIED" },
      { id: "demo-battery-2", date: shiftedIso(-45).slice(0, 10), soh: 94, delta: 4, mileage: 27480, source: "PRECISION_VERIFIED" },
    ],
    passport: {
      number: "PEV-DEMO-2401",
      status: "ACTIVE",
      isOwner: true,
      accessRole: "OWNER",
      owner: { id: "demo-user", name: "Cliente Demo", email: "developer.demo@precision-ev.test" },
      permissions: { canManageServices: true, canEditVehicle: true },
      driversCount: 0,
      removalPolicy: { canDelete: false, workshopLinked: true, reason: "PRECISION_HISTORY_PROTECTED" },
      createdAt: shiftedIso(-250),
      updatedAt: shiftedIso(-1),
    },
    services: [
      { id: "demo-service-1", source: "PRECISION", status: "COMPLETED", title: "Inspeção anual", completedAt: shiftedIso(-180), odometer: 22410, createdAt: shiftedIso(-190), updatedAt: shiftedIso(-180) },
    ],
  };

  return enrichDemoSeed({
    profile: {
      id: "demo-user",
      email: "developer.demo@precision-ev.test",
      firstName: "Cliente",
      lastName: "Demo",
      language: "pt",
      role: "CUSTOMER",
      phone: "+41 79 000 00 00",
      address: "Rua de Demonstração 1\n4416 Bubendorf",
      profilePhoto: "",
      notificationsEnabled: false,
      emailVerified: true,
    },
    vehicles: [vehicle],
    assistance: {
      [vehicleId]: {
        thread: null,
        messages: [],
        workshop: {
          name: "Precision Autotechnik AG",
          address: "Wattwerkstrasse 2, 4416 Bubendorf",
          phone: "+41 61 951 20 20",
          email: "info@precisionautotechnik.com",
          hours: "Seg.–Sex. 07:30–17:30 · Sáb. 09:00–12:00",
        },
        dossier: null,
      },
    },
  }, demoPdf, createDemoDossier, advanceDemoWorkshop);
}

function loadState(): DemoState {
  if (volatileState) return volatileState;
  try {
    const stored = localStorage.getItem(DEMO_STATE_KEY);
    if (stored) {
      volatileState = JSON.parse(stored) as DemoState;
      if (!volatileState.extras.assistanceV84) {
        for (const [index,vehicle] of volatileState.vehicles.entries()) {
          if(!vehicle.id.startsWith("demo-vehicle-precision-"))continue;
          vehicle.tyreFront ||= index===0?"235/45 R18":index===1?"245/45 R19":"215/55 R18";
          vehicle.tyreRear ||= index===1?"275/40 R19":vehicle.tyreFront;
          const filter=vehicle.events.find(e=>e.id===`${vehicle.id}-service-1`), brakes=vehicle.events.find(e=>e.id===`${vehicle.id}-service-2`);
          if(index<2&&filter&&!filter.nextDueDate){filter.category="cabin_filter";filter.nextDueDate=new Date(Date.now()-86400000).toISOString().slice(0,10);}
          if(index===0&&brakes&&!brakes.nextDueDate){brakes.category="brakes";brakes.nextDueDate=new Date(Date.now()-86400000).toISOString().slice(0,10);}
        }
        volatileState.extras.assistanceV84=true;
      }
      for (const assistance of Object.values(volatileState.assistance || {})) {
        const dossier = assistance?.dossier && typeof assistance.dossier === "object" ? assistance.dossier as Record<string, unknown> : null;
        const appointment = dossier?.appointment && typeof dossier.appointment === "object" ? dossier.appointment as Record<string, unknown> : null;
        if (appointment && (!Array.isArray(appointment.preApprovedSlots) || !appointment.preApprovedSlots.length)) appointment.preApprovedSlots = defaultPreApprovedSlots();
        const payment = dossier?.payment && typeof dossier.payment === "object" ? dossier.payment as Record<string, unknown> : null;
        if (payment) payment.methods = { account_card: true, paypal: true, google_pay: true, apple_pay: true, twint: true, local: true };
      }
      saveDemoState(volatileState);
      return volatileState;
    }
  } catch {
    // A fresh isolated scenario is safer than a partially corrupted demo.
  }
  const state = createSeed();
  saveDemoState(state);
  return state;
}

function saveDemoState(state: DemoState) {
  volatileState = state;
  for (const vehicle of state.vehicles) {
    const dossier = state.assistance[vehicle.id]?.dossier;
    if (dossier) vehicle.dossierSnapshot = clone(dossier as Record<string, unknown>);
    else delete vehicle.dossierSnapshot;
  }
  try { localStorage.setItem(DEMO_STATE_KEY, JSON.stringify(state)); } catch {
    // Safari private mode can reject persistent storage; the in-memory request still remains usable.
  }
}

function json(payload: unknown, status = 200) {
  return new Response(JSON.stringify(payload), {
    status,
    headers: { "content-type": "application/json; charset=utf-8", "cache-control": "no-store" },
  });
}

function parseBody(init: RequestInit) {
  if (typeof init.body !== "string") return {} as Record<string, unknown>;
  try { return JSON.parse(init.body) as Record<string, unknown>; } catch { return {} as Record<string, unknown>; }
}

const idForDemo=()=>`demo-${crypto.randomUUID()}`;

function timelineEvent(title: string, body: string) {
  return { id: `demo-event-${crypto.randomUUID()}`, title, body, createdAt: new Date().toISOString() };
}

function vehicleOverview(state: DemoState, vehicleId: string) {
  return state.assistance[vehicleId] || null;
}

function updateVehicleSummary(state: DemoState, vehicleId: string, activity: string) {
  const vehicle = state.vehicles.find((item) => item.id === vehicleId);
  if (!vehicle) return;
  vehicle.workshopActivity = activity;
  vehicle.workshopUpdatedAt = new Date().toISOString();
  const overview = vehicleOverview(state, vehicleId);
  if (overview?.dossier) vehicle.dossierSnapshot = clone(overview.dossier as Record<string, unknown>);
}

function addTimeline(overview: Record<string, unknown>, title: string, body: string) {
  const dossier = overview.dossier as Record<string, unknown>;
  const timeline = Array.isArray(dossier.timeline) ? dossier.timeline : [];
  dossier.timeline = [...timeline, timelineEvent(title, body)];
}

function addWorkshopMessage(overview: Record<string, unknown>, body: string) {
  const messages = Array.isArray(overview.messages) ? overview.messages as Record<string, unknown>[] : [];
  messages.push({ id: `demo-message-${crypto.randomUUID()}`, sender: "WORKSHOP", body, createdAt: new Date().toISOString(), attachments: [] });
  overview.messages = messages;
}

function setVehicleStage(state: DemoState, vehicleId: string, status: string, progress: number, activity: string) {
  const vehicle = state.vehicles.find((item) => item.id === vehicleId);
  if (!vehicle) return;
  vehicle.workshopActive = status !== "Serviço concluído";
  vehicle.workshopStatus = status;
  vehicle.workshopProgress = progress;
  vehicle.workshopActivity = activity;
  vehicle.workshopUpdatedAt = new Date().toISOString();
  vehicle.lastUpdated = vehicle.workshopUpdatedAt;
  vehicle.tvpfSections = [
    { section: "Receção", completed: progress >= 5 ? 4 : 0, total: 4 },
    { section: "Diagnóstico", completed: progress >= 45 ? 10 : progress >= 30 ? 6 : 0, total: 10 },
    { section: "Reparação", completed: progress >= 92 ? 8 : progress >= 70 ? 3 : 0, total: 8 },
    { section: "Controlo final", completed: progress >= 100 ? 6 : progress >= 92 ? 3 : 0, total: 6 },
  ];
}

export function tickDemoState(state = loadState()) {
  if(state.speed==="paused")return false;
  let changed=false;
  for(const request of state.extras.requests){if(request.status==="received"&&request.dueAt<=Date.now()){request.status="confirmed";request.response="A equipa simulada recebeu e confirmou o pedido.";changed=true;}}
  for(const [vehicleId,overview] of Object.entries(state.assistance)){
    const queue=(overview.pendingReplies || []) as {body:string;dueAt:number}[];
    const due=queue.filter(item=>item.dueAt<=Date.now());
    for(const item of due){addWorkshopMessage(overview,item.body);changed=true;}
    overview.pendingReplies=queue.filter(item=>item.dueAt>Date.now());
    if(!overview.dossier)continue;
    if(!overview.nextWorkshopAt){overview.nextWorkshopAt=Date.now()+demoDelay(state);changed=true;}
    if(Number(overview.nextWorkshopAt)>Date.now())continue;
    const result=advanceDemoWorkshop(state,vehicleId,overview);
    if(result.status===200){recordDemoOperation(state,vehicleId,overview,String(result.stage));changed=true;}
    overview.nextWorkshopAt=Date.now()+demoDelay(state);
  }
  if(changed)saveDemoState(state);
  return changed;
}
export function startDemoSimulation(onChange:()=>void) {
  const run=()=>{if(!document.hidden&&tickDemoState())onChange();};
  const timer=window.setInterval(run,1000);
  const stored=(e:StorageEvent)=>{if(e.key===DEMO_STATE_KEY){volatileState=null;onChange();}};
  window.addEventListener("storage",stored);window.addEventListener("focus",run);
  return ()=>{clearInterval(timer);window.removeEventListener("storage",stored);window.removeEventListener("focus",run);};
}
function advanceDemoWorkshop(state: DemoState, vehicleId: string, overview: Record<string, unknown>) {
  const dossier = overview.dossier as Record<string, unknown> | null;
  if (!dossier) return { error: "DEMO_REQUEST_REQUIRED", status: 409 };
  const appointment = dossier.appointment as Record<string, unknown>;
  const initial = dossier.initialDiagnostic as Record<string, unknown>;
  const payment = dossier.payment as Record<string, unknown>;
  const pickup = dossier.pickup as Record<string, unknown>;
  const dropoff = dossier.dropoff as Record<string, unknown>;
  const afterHours = dropoff.afterHoursRequest as Record<string, unknown>;
  const quotes = dossier.quotes as Record<string, unknown>[];
  const stage = String(dossier.demoStage || "request_received");

  if (String(afterHours?.status || "") === "pending") {
    afterHours.status = "approved";
    afterHours.approvedAt = new Date().toISOString();
    const operations=(dossier.operations ||= {events:[],rentals:[],quality:{}}) as {events:Record<string,unknown>[]};
    operations.events.unshift({id:idForDemo(),source:"safes",title:"Cofre atribuído",operator:"Receção · Demo",body:"Cofre 3",createdAt:new Date().toISOString()});
    (dropoff.locker as Record<string, unknown>).code = String(1000 + Math.floor(Math.random()*9000));
    addTimeline(overview, "Cofre de entrega atribuído", "Cofre 3 · código disponibilizado ao cliente.");
    addWorkshopMessage(overview, "O seu cofre para entrega fora do horário está preparado. O código já está disponível na marcação.");
    return { stage, status: 200 };
  }

  if (String(appointment.status || "") === "change_requested") {
    appointment.scheduledAt = appointment.requestedScheduledAt;
    appointment.requestedScheduledAt = "";
    appointment.status = "proposed";
    appointment.workshopChangeReason = "A oficina aceitou a data solicitada. Confirme para concluir a marcação.";
    dossier.demoStage = "appointment_proposed";
    addTimeline(overview, "Nova data aceite pela oficina", "A data solicitada pelo cliente foi proposta para confirmação.");
    addWorkshopMessage(overview, "A nova data está disponível. Por favor, confirme a marcação na área Recursos.");
    setVehicleStage(state, vehicleId, "Marcação proposta", 8, "Confirme a nova data sugerida pela oficina.");
    return { stage: "appointment_proposed", status: 200 };
  }

  if (String(appointment.status || "") === "cancelled" && stage !== "complete") {
    appointment.status = "proposed";
    if (!Array.isArray(appointment.preApprovedSlots) || !appointment.preApprovedSlots.length) appointment.preApprovedSlots = defaultPreApprovedSlots();
    appointment.scheduledAt = String((appointment.preApprovedSlots as string[])[0] || localDemoSlot(nextBusinessAppointment()));
    appointment.rentalCarRequested = null;
    appointment.workshopChangeReason = "Nova proposta enviada após o cancelamento anterior.";
    dossier.demoStage = "appointment_proposed";
    addTimeline(overview, "Nova marcação proposta", "A oficina enviou uma nova possibilidade de data.");
    addWorkshopMessage(overview, "Enviámos uma nova proposta de marcação para sua confirmação.");
    setVehicleStage(state, vehicleId, "Marcação proposta", 8, "Confirme a data sugerida pela oficina.");
    return { stage: "appointment_proposed", status: 200 };
  }

  const pendingQuote = quotes.find((item) => String(item.status) === "pending");
  const rejectedQuote = [...quotes].reverse().find((item) => ["rejected", "contact"].includes(String(item.status)));
  if (stage === "quote_pending" && !pendingQuote && rejectedQuote) {
    const originalItems=Array.isArray(rejectedQuote.items)?rejectedQuote.items as Record<string,unknown>[]:[];
    const items=originalItems.map(item=>({...item,unitPrice:Math.round(Number(item.unitPrice)*.9*100)/100,total:Math.round(Number(item.total)*.9*100)/100}));
    const revised = {id:idForDemo(),version:quotes.length+1,status:"pending",title:"Proposta revista: "+String(rejectedQuote.title||"trabalhos"),description:"Proposta alternativa simulada, com desconto de 10%. Requer nova aprovação.",amountIncludesVat:true,items,total:Math.round(items.reduce((sum,item)=>sum+item.total,0)*100)/100,createdAt:new Date().toISOString()};
    quotes.push(revised);
    addTimeline(overview, "Orçamento revisto enviado", `CHF ${revised.total.toFixed(2)} · IVA incluído`);
    addWorkshopMessage(overview, `Revimos os trabalhos e enviámos a versão ${revised.version} do orçamento para decisão.`);
    setVehicleStage(state, vehicleId, "Orçamento revisto", 45, "A sua aprovação do novo orçamento é necessária.");
    return { stage, status: 200 };
  }

  if (stage === "diagnostic_authorization" && ["rejected","contact"].includes(String(initial.status))) {
    initial.status = "pending";
    addTimeline(overview, "Autorização de diagnóstico reenviada", "CHF 184.85 · IVA incluído");
    addWorkshopMessage(overview, "Voltámos a disponibilizar a autorização do diagnóstico. Contacte-nos pelo chat se precisar de esclarecimentos.");
    setVehicleStage(state, vehicleId, "Autorização necessária", 12, "A sua aprovação do diagnóstico é necessária.");
    return { stage, status: 200 };
  }

  const changes = (dossier.changeRequests || []) as Record<string,unknown>[];
  const pendingChange=changes.find(r=>r.status==="pending");
  if(pendingChange){
    pendingChange.status="reviewed";
    addTimeline(overview,"Pedido de alteração analisado","A oficina respondeu ao pedido de alteração.");
    addWorkshopMessage(overview,"Analisámos a alteração: "+String(pendingChange.body||"")+". Pode consultar os detalhes e confirmar a proposta em Recursos.");
    if(pendingChange.kind==="appointment"){appointment.status="proposed";appointment.scheduledAt=defaultPreApprovedSlots()[Math.floor(Math.random()*5)];dossier.demoStage="appointment_proposed";}
    return {stage:String(dossier.demoStage),status:200};
  }
  const preparation:Record<string,[string,string,number]>={certified_inspection:["test_drive","Test Drive",94],test_drive:["washing","Lavagem",96],washing:["finishing","Acabamentos finais",97],finishing:["quality_control","Controlo final",98]};
  if(preparation[stage]){
    const [next,title,progress]=preparation[stage];dossier.demoStage=next;
    addTimeline(overview,title,"Atividade simulada da oficina.");addWorkshopMessage(overview,"Atualização do seu veículo: "+title+".");
    setVehicleStage(state,vehicleId,title,progress,title+" em curso.");
    return {stage:next,status:200};
  }
  switch (stage) {
    case "request_received":
      appointment.status = "proposed";
      if (!Array.isArray(appointment.preApprovedSlots) || !appointment.preApprovedSlots.length) appointment.preApprovedSlots = defaultPreApprovedSlots();
      appointment.scheduledAt = String((appointment.preApprovedSlots as string[]).find((slot) => slot === localDemoSlot(nextBusinessAppointment())) || (appointment.preApprovedSlots as string[])[0] || localDemoSlot(nextBusinessAppointment()));
      appointment.proposedAt = new Date().toISOString();
      dossier.demoStage = "appointment_proposed";
      addTimeline(overview, "Marcação proposta", "A oficina enviou uma proposta de data ao cliente.");
      addWorkshopMessage(overview, "Recebemos o seu pedido. Enviámos uma proposta de marcação para sua confirmação.");
      setVehicleStage(state, vehicleId, "Marcação proposta", 8, "Confirme a data sugerida pela oficina.");
      break;
    case "appointment_confirmed":
      initial.status = "pending";
      initial.amount = 184.85;
      initial.requestedAt = new Date().toISOString();
      dossier.demoStage = "diagnostic_authorization";
      addTimeline(overview, "Autorização do diagnóstico inicial solicitada", "CHF 184.85 · IVA incluído");
      addWorkshopMessage(overview, "A marcação está confirmada. Precisamos agora da autorização para o diagnóstico inicial.");
      setVehicleStage(state, vehicleId, "Autorização necessária", 12, "A sua aprovação do diagnóstico é necessária.");
      break;
    case "diagnostic_approved":
      dropoff.workshopConfirmedAt = new Date().toISOString();
      if (appointment.rentalCarRequested === true) (appointment.rentalCar as Record<string, unknown>).startedAt = new Date().toISOString();
      dossier.demoStage = "diagnosis_in_progress";
      dossier.deliveryEstimate = "Diagnóstico em curso. Atualização prevista durante o dia.";
      addTimeline(overview, "Veículo recebido pela oficina", "O diagnóstico inicial foi iniciado.");
      addWorkshopMessage(overview, "O veículo foi recebido e o diagnóstico já está em curso.");
      setVehicleStage(state, vehicleId, "Diagnóstico em curso", 30, "A oficina está a verificar o veículo.");
      break;
    case "diagnosis_in_progress": {
      const vehicle=state.vehicles.find(v=>v.id===vehicleId);
      const request=String(dossier.request||"").toLowerCase();
      const choices=/revis|manuten|inspe|viagem/.test(request)
        ? [{title:"Revisão e preparação para viagem",finding:"Inspeção efetuada. Recomendamos substituir o filtro do habitáculo e renovar o líquido dos travões.",items:[{description:"Filtro do habitáculo e substituição",quantity:1,unitPrice:89,total:89},{description:"Renovação do líquido dos travões",quantity:1,unitPrice:145,total:145}]}]
        : [{title:"Suspensão e alinhamento",finding:"Foi identificada folga num braço da suspensão dianteira.",items:[{description:"Braço da suspensão",quantity:1,unitPrice:389,total:389},{description:"Mão de obra",quantity:1,unitPrice:134.6,total:134.6},{description:"Alinhamento",quantity:1,unitPrice:118,total:118}]},{title:"Travões e verificação de segurança",finding:"Detetámos desgaste nas pastilhas e recomendamos a substituição.",items:[{description:"Pastilhas de travão",quantity:1,unitPrice:180,total:180},{description:"Substituição e verificação",quantity:1,unitPrice:125,total:125}]}];
      const proposal=choices[Math.floor(Math.random()*choices.length)];
      const total=Math.round(proposal.items.reduce((sum,item)=>sum+item.total,0)*100)/100;
      dossier.diagnosis=proposal.finding;
      dossier.deliveryEstimate="Conclusão prevista após aprovação e execução dos trabalhos. O tempo está acelerado nesta demo.";
      const report=demoPdf("Relatorio do diagnostico DEMO",[`Auftrag: ${String(dossier.dossierNo)}`,`Veiculo: ${vehicle?.name}`,proposal.finding,`SOH: ${vehicle?.soh}%`,"Documento ficticio, sem validade comercial."]);
      (dossier.attachments as Record<string,unknown>[]).push({id:idForDemo(),name:"Diagnostico_DEMO.pdf",caption:"Relatório do diagnóstico",mime:"application/pdf",...report,createdAt:new Date().toISOString()});
      quotes.push({id:idForDemo(),version:quotes.length+1,status:"pending",title:proposal.title,description:proposal.finding,total,amountIncludesVat:true,items:proposal.items,createdAt:new Date().toISOString()});
      dossier.demoStage="quote_pending";
      addTimeline(overview,"Diagnóstico concluído",proposal.finding);
      addTimeline(overview,"Orçamento enviado",`CHF ${total.toFixed(2)} · diagnóstico contabilizado separadamente`);
      addWorkshopMessage(overview,"Concluímos o diagnóstico. Consulte o relatório e decida sobre o orçamento em Recursos.");
      setVehicleStage(state,vehicleId,"Orçamento pendente",45,"A sua aprovação é necessária.");
      if(vehicle)vehicle.tvpfHighlights=[{section:"Inspeção",checkpoint:"Diagnóstico técnico",status:"Atenção",result:proposal.finding,procedure:["Leitura eletrónica","Inspeção visual","Relatório e orçamento"],documents:[{name:"Diagnostico_DEMO.pdf",type:"application/pdf",...report}]},{section:"Bateria",checkpoint:"Saúde da bateria",status:"OK",result:`SOH ${vehicle.soh}% · Delta ${vehicle.delta} mV`}];
      break;
    }
    case "quote_approved":
      dossier.demoStage = "repair_in_progress";
      addTimeline(overview, "Reparação iniciada", "Execução dos trabalhos aprovados em curso.");
      addWorkshopMessage(overview, "Recebemos a sua aprovação e iniciámos a reparação.");
      setVehicleStage(state, vehicleId, "Reparação em curso", 70, "Os trabalhos aprovados estão em execução.");
      break;
    case "repair_in_progress":
      dossier.demoStage = "certified_inspection";
      addTimeline(overview, "Reparação concluída", "O veículo entrou no controlo final de qualidade.");
      addWorkshopMessage(overview, "A reparação terminou. Estamos a realizar o controlo final.");
      setVehicleStage(state, vehicleId, "Inspeção certificada", 92, "A oficina está a realizar a inspeção certificada.");
      break;
    case "quality_control": {
      const approvedQuote = [...quotes].reverse().find((item) => String(item.status) === "approved");
      const rentalCar = appointment.rentalCar as Record<string, unknown>;
      const rentalStart = new Date(String(rentalCar?.startedAt || appointment.confirmedAt || new Date().toISOString()));
      const rentalDays = appointment.rentalCarRequested === true ? Math.max(1, Math.floor((Date.now() - rentalStart.getTime()) / 86400000) + 1) : 0;
      const rentalAmount = rentalDays * Number(rentalCar?.dailyRate || 60);
      if (rentalCar) { rentalCar.daysCharged = rentalDays; rentalCar.currentTotal = rentalAmount; }
      payment.amount = Math.round((Number(initial.amount || 0) + Number(approvedQuote?.total || 0) + rentalAmount) * 100) / 100;
      payment.status = "pending";
      dossier.demoStage = "payment_due";
      dossier.deliveryEstimate = "Serviço concluído. Escolha o método de pagamento para preparar o levantamento.";
      addTimeline(overview, "Serviço concluído", `Valor final CHF ${Number(payment.amount).toFixed(2)} · IVA incluído${rentalAmount ? ` · inclui ${rentalDays} dia${rentalDays === 1 ? "" : "s"} de carro de substituição` : ""}`);
      addWorkshopMessage(overview, "O serviço e o controlo final estão concluídos. Já pode escolher o método de pagamento.");
      setVehicleStage(state, vehicleId, "Pagamento pendente", 100, "Escolha o método de pagamento.");
      break;
    }
    case "payment_selected":
      payment.status = "paid";
      recordDemoOperation(state,vehicleId,overview,"payment_confirmed");
      payment.paidAt = new Date().toISOString();
      pickup.authorizedAt = new Date().toISOString();
      (pickup.locker as Record<string,unknown>).code ||= String(1000+Math.floor(Math.random()*9000));
      dossier.demoStage = "payment_confirmed";
      addTimeline(overview, "Pagamento confirmado", `CHF ${Number(payment.amount || 0).toFixed(2)} · pago`);
      addWorkshopMessage(overview, "Pagamento confirmado. Escolha agora como pretende levantar o veículo.");
      setVehicleStage(state, vehicleId, "Pronto para levantamento", 100, "Pagamento confirmado. Escolha a forma de levantamento.");
      break;
    case "pickup_selected": {
      dossier.demoStage = "complete";
      dossier.deliveryEstimate = "Veículo entregue. O serviço e os documentos encontram-se arquivados no histórico.";
      {
        const invoice = demoPdf("Fatura demonstrativa", [`Dossie: ${String(dossier.dossierNo)}`, `Valor final: CHF ${Number(payment.amount || 0).toFixed(2)}`, "IVA de 8,1% incluido.", "Documento demonstrativo sem validade fiscal."]);
        const report = demoPdf("Relatorio final da intervencao", [`Dossie: ${String(dossier.dossierNo)}`, "Diagnostico, reparacao e controlo final concluidos.", "O veiculo foi entregue ao cliente.", "Documento demonstrativo sem validade comercial."]);
        (dossier.attachments as Record<string, unknown>[]).push(
          { id: "demo-doc-2", name: `Fatura_${String(dossier.dossierNo)}.pdf`, caption: "Fatura do serviço", mime: "application/pdf", size: invoice.size, url: invoice.url },
          { id: "demo-doc-3", name: `Relatorio_${String(dossier.dossierNo)}.pdf`, caption: "Relatório final da intervenção", mime: "application/pdf", size: report.size, url: report.url },
        );
      }
      pickup.completedAt = new Date().toISOString();
      addTimeline(overview, "Veículo entregue ao cliente", "Dossiê concluído e arquivado no histórico do veículo.");
      addWorkshopMessage(overview, "O veículo foi entregue. Obrigado por escolher a Precision Autotechnik.");
      if (overview.thread && typeof overview.thread === "object") (overview.thread as Record<string, unknown>).status = "CLOSED";
      setVehicleStage(state, vehicleId, "Serviço concluído", 100, "Veículo entregue. Consulte o histórico e os documentos.");
      const vehicle = state.vehicles.find((item) => item.id === vehicleId);
      if (vehicle) {
        const archivedDocuments = (dossier.attachments as Record<string, unknown>[]).filter((item) => ["demo-doc-2", "demo-doc-3"].includes(String(item.id))).map((item) => ({ name: String(item.name || "Documento.pdf"), type: String(item.mime || "application/pdf"), size: Number(item.size || 0), url: String(item.url || "") }));
        vehicle.events.unshift({ id: `demo-complete-${crypto.randomUUID()}`, type: "maintenance", title: String((overview.thread as Record<string,unknown>)?.subject || "Serviço concluído"), value: `Concluída · CHF ${Number(payment.amount || 0).toFixed(2)}`, date: new Date().toISOString().slice(0, 10), source: "PRECISION_VERIFIED", amount: Number(payment.amount || 0), odometer: vehicle.mileage, workshop: "Precision Autotechnik", verifiedByPrecision: true, serviceType: "repaired", documents: archivedDocuments });
        vehicle.services = [...(vehicle.services || []), { id: `demo-service-${crypto.randomUUID()}`, source: "PRECISION", status: "COMPLETED", title: String((overview.thread as Record<string,unknown>)?.subject || "Serviço concluído"), completedAt: new Date().toISOString(), odometer: vehicle.mileage, createdAt: String((overview.thread as Record<string, unknown>)?.createdAt || new Date().toISOString()), updatedAt: new Date().toISOString() }];
      }
      break;
    }
    default:
      return { error: "DEMO_WAITING_FOR_CUSTOMER", status: 409 };
  }
  return { stage: String(dossier.demoStage), status: 200 };
}

async function fileUrl(file: File) {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

function asciiPdfText(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\x20-\x7E]/g, " ").replace(/([\\()])/g, "\\$1");
}

function wrapPdfText(value: string, width = 78) {
  const words = asciiPdfText(value).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (`${current} ${word}`.trim().length > width && current) { lines.push(current); current = word; }
    else current = `${current} ${word}`.trim();
  }
  if (current) lines.push(current);
  return lines;
}

export function demoPdf(title: string, lines: string[], signatureDataUrl = "") {
  const jpegBase64 = signatureDataUrl.startsWith("data:image/jpeg") ? signatureDataUrl.split(",")[1] || "" : "";
  const jpeg = jpegBase64 ? atob(jpegBase64) : "";
  let content = `BT\n/F1 18 Tf\n50 790 Td\n(${asciiPdfText(title)}) Tj\n/F1 10 Tf\n0 -30 Td\n`;
  for (const line of lines.flatMap((item) => wrapPdfText(item))) content += `(${line}) Tj\n0 -17 Td\n`;
  content += "ET\n";
  if (jpeg) content += "q\n360 0 0 115 50 165 cm\n/Im1 Do\nQ\nBT\n/F1 9 Tf\n50 145 Td\n(Assinatura digital do cliente) Tj\nET\n";
  const resources = jpeg ? "/Resources << /Font << /F1 5 0 R >> /XObject << /Im1 6 0 R >> >>" : "/Resources << /Font << /F1 5 0 R >> >>";
  const objects = [
    "1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n",
    "2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n",
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] ${resources} /Contents 4 0 R >>\nendobj\n`,
    `4 0 obj\n<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`,
    "5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n",
    ...(jpeg ? [`6 0 obj\n<< /Type /XObject /Subtype /Image /Width 720 /Height 230 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n${jpeg}\nendstream\nendobj\n`] : []),
  ];
  let binary = "%PDF-1.4\n%\xE2\xE3\xCF\xD3\n";
  const offsets = [0];
  for (const object of objects) { offsets.push(binary.length); binary += object; }
  const xref = binary.length;
  binary += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index <= objects.length; index += 1) binary += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
  binary += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return { url: `data:application/pdf;base64,${btoa(binary)}`, size: binary.length };
}

export function isDemoApiBase(apiBase: string) {
  return apiBase === DEMO_API_BASE;
}

export function resetDemoEnvironment() {
  volatileState = null;
  try {
    localStorage.removeItem(DEMO_STATE_KEY);
    localStorage.removeItem(DEMO_VEHICLES_KEY);
    localStorage.removeItem(DEMO_SETTINGS_KEY);
    localStorage.removeItem("precisionEV.demo.shortcuts");
    localStorage.removeItem("precisionEV.demo.preferenceAccount");
    localStorage.removeItem("precisionEV.preferences.v1.demo-user");
  } catch { /* The fresh demo seed remains available when storage is blocked. */ }
}

export async function demoFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const state = loadState();
  tickDemoState(state);
  const method = String(init.method || "GET").toUpperCase();

  if (path === "/api/v1/session" && method === "GET") return json({ authenticated: true, account: state.profile });
  if (path === "/api/v1/vehicles" && method === "GET") return json({ vehicles: state.vehicles });
  if (path === "/api/v1/vehicles" && method === "PUT") {
    const body = parseBody(init);
    if (Array.isArray(body.vehicles)) state.vehicles = (body.vehicles as Vehicle[]).map(incoming => {
      const current = state.vehicles.find(v=>v.id===incoming.id);
      if (!state.assistance[incoming.id]) state.assistance[incoming.id]={thread:null,dossier:null,messages:[],workshop:{}};
      return {...incoming,...(current ? Object.fromEntries(Object.entries(current).filter(([key])=>key.startsWith("workshop")||key.startsWith("dossier")||key.startsWith("tvpf"))) : {})};
    });
    saveDemoState(state);
    return json({ updated: true, vehicles: state.vehicles });
  }
  if (path === "/api/v1/preferences" && method === "PATCH") {
    const body = parseBody(init);
    state.profile = {...state.profile, preferences: {...(state.profile.preferences as object || {}), ...body}, ...(body.language ? {language:body.language} : {})};
    saveDemoState(state);
    return json({updated:true, account:state.profile});
  }
  if (path === "/api/v1/profile" && method === "PATCH") {
    const body = parseBody(init);
    const names = String(body.fullName || "Cliente Demo").trim().split(/\s+/);
    state.profile = { ...state.profile, firstName: names.shift() || "Cliente", lastName: names.join(" ") || "Demo", phone: body.phone || "", address: body.address || "", profilePhoto: body.profilePhoto || "", notificationsEnabled: Boolean(body.notificationsEnabled) };
    saveDemoState(state);
    return json({ updated: true, account: state.profile });
  }

  const workshopAdvanceMatch = path.match(/^\/api\/v1\/demo\/vehicles\/([^/]+)\/workshop\/advance$/);
  if (workshopAdvanceMatch && method === "POST") {
    const vehicleId = decodeURIComponent(workshopAdvanceMatch[1]);
    const overview = vehicleOverview(state, vehicleId);
    if (!overview) return json({ error: "DEMO_VEHICLE_NOT_FOUND" }, 404);
    const result = advanceDemoWorkshop(state, vehicleId, overview);
    if(result.status===200)recordDemoOperation(state,vehicleId,overview,String(result.stage));
    overview.nextWorkshopAt=Date.now()+demoDelay(state);
    saveDemoState(state);
    if (result.status !== 200) return json({ error: result.error, ...overview }, result.status);
    return json({ ...overview, updated: true, demoStage: result.stage });
  }

  const workshopMessageMatch = path.match(/^\/api\/v1\/demo\/vehicles\/([^/]+)\/workshop\/message$/);
  if (workshopMessageMatch && method === "POST") {
    const vehicleId = decodeURIComponent(workshopMessageMatch[1]);
    const overview = vehicleOverview(state, vehicleId);
    if (!overview) return json({ error: "DEMO_VEHICLE_NOT_FOUND" }, 404);
    const body = parseBody(init);
    const message = String(body.body || "").trim();
    if (message.length < 2) return json({ error: "MESSAGE_REQUIRED" }, 400);
    addWorkshopMessage(overview, message);
    updateVehicleSummary(state, vehicleId, "Recebeu uma nova mensagem da oficina.");
    saveDemoState(state);
    return json({ ...overview, created: true });
  }

  const assistanceMatch = path.match(/^\/api\/v1\/vehicles\/([^/]+)\/assistance(?:\/(requests|messages|attachments|read))?$/);
  if (assistanceMatch) {
    const vehicleId = decodeURIComponent(assistanceMatch[1]);
    const operation = assistanceMatch[2];
    const overview = vehicleOverview(state, vehicleId);
    if (!overview) return json({ error: "DEMO_VEHICLE_NOT_FOUND" }, 404);
    if (!operation && method === "GET") return json(overview);
    if (operation === "read" && method === "POST") {
      const body = parseBody(init);
      const thread = overview.thread as Record<string, unknown> | null;
      if (body.threadId !== thread?.id) return json({error:"CONVERSATION_CHANGED"},409);
      const ids = Array.isArray(body.messageIds) ? body.messageIds : [];
      const accepted = (overview.messages as Record<string, unknown>[]).filter(m => m.sender === "WORKSHOP" && ids.includes(m.id));
      accepted.forEach(m => { m.readAt ||= new Date().toISOString(); });
      saveDemoState(state);
      return json({readMessageIds:accepted.map(m => m.id)});
    }
    if (operation === "requests" && method === "POST") {
      const body = parseBody(init);
      const now = new Date().toISOString();
      if ((overview.dossier as Record<string,unknown>)?.demoStage==="complete") {
        state.archives ||= [];
        state.archives.push(clone(overview));
        overview.thread=null; overview.dossier=null; overview.messages=[];
      }
      const dossierNumber = String((overview.dossier as Record<string,unknown>)?.dossierNo || `DEMO-${++state.sequence}`);
      const thread = overview.thread && typeof overview.thread === "object" ? overview.thread as Record<string, unknown> : {
        id: `demo-thread-${crypto.randomUUID()}`,
        vehicleId,
        userId: "demo-user",
        status: "OPEN",
        dossierId: `demo-dossier-${dossierNumber}`,
        dossierNumber,
        syncStatus: "SYNCED",
        createdAt: now,
      };
      thread.category = body.category || "OTHER";
      thread.subject = body.subject || "Pedido demo";
      thread.updatedAt = now;
      overview.thread = thread;
      const acceptedAt = String(body.termsAcceptedAt || now);
      const requestText = String(body.body || "Pedido de assistência demo");
      if (!overview.dossier) {
        const dossier = createDemoDossier(requestText, acceptedAt);
        dossier.dossierNo = dossierNumber;
        dossier.initialDiagnostic.title = `Diagnóstico: ${String(body.subject || "pedido do cliente")}`;
        const signedPdf = demoPdf("Confirmacao do pedido de assistencia", [
          `Dossie: ${String(dossier.dossierNo)}`,
          `Cliente: Cliente Demo`,
          `Veiculo: ${state.vehicles.find((item) => item.id === vehicleId)?.name || "Veiculo demo"}`,
          Number.isFinite(Number(body.mileage)) ? `Quilometragem confirmada: ${Math.round(Number(body.mileage))} km` : "",
          `Pedido: ${requestText}`,
          `Aceite em: ${new Date(acceptedAt).toLocaleString("pt-PT", { hour12: false })}`,
          "O cliente confirmou que o pedido corresponde ao servico pretendido e aceitou os termos e condicoes da Precision Autotechnik.",
        ].filter(Boolean), String(body.signature || ""));
        (dossier.attachments as Record<string, unknown>[])[0] = { id: "demo-terms-acceptance", name: "Aceitacao_cliente_DEMO.pdf", caption: "Pedido e termos aceites", mime: "application/pdf", size: signedPdf.size, url: signedPdf.url, createdAt: acceptedAt };
        overview.dossier = dossier;
      } else {
        const dossier = overview.dossier as Record<string, unknown>;
        const currentRequest = String(dossier.request || "").trim();
        dossier.request = currentRequest ? `${currentRequest}\n\nPEDIDO ADICIONAL\n${requestText}` : requestText;
        const timeline = Array.isArray(dossier.timeline) ? dossier.timeline as Record<string, unknown>[] : [];
        timeline.push({ id: `demo-event-request-${crypto.randomUUID()}`, title: "Problema adicional enviado", body: requestText, createdAt: now });
        timeline.push({ id: `demo-event-terms-${crypto.randomUUID()}`, title: "Pedido adicional e termos aceites", body: "Aceitação digital assinada pelo cliente.", createdAt: acceptedAt });
        dossier.timeline = timeline;
        const signedPdf = demoPdf("Confirmacao de pedido adicional", [
          `Dossie: ${String(dossier.dossierNo)}`,
          "Cliente: Cliente Demo",
          `Veiculo: ${state.vehicles.find((item) => item.id === vehicleId)?.name || "Veiculo demo"}`,
          Number.isFinite(Number(body.mileage)) ? `Quilometragem confirmada: ${Math.round(Number(body.mileage))} km` : "",
          `Pedido adicional: ${requestText}`,
          `Aceite em: ${new Date(acceptedAt).toLocaleString("pt-PT", { hour12: false })}`,
          "O cliente confirmou o pedido adicional e aceitou os termos e condicoes da Precision Autotechnik.",
        ].filter(Boolean), String(body.signature || ""));
        const attachments = Array.isArray(dossier.attachments) ? dossier.attachments as Record<string, unknown>[] : [];
        attachments.push({ id: `demo-additional-terms-${crypto.randomUUID()}`, name: `Pedido_adicional_${attachments.length + 1}_DEMO.pdf`, caption: "Pedido adicional e termos aceites", mime: "application/pdf", size: signedPdf.size, url: signedPdf.url, createdAt: acceptedAt });
        dossier.attachments = attachments;
      }
      const messages = overview.messages as Record<string, unknown>[];
      messages.push({ id: `demo-message-${crypto.randomUUID()}`, sender: "CUSTOMER", body: String(body.body || ""), createdAt: now, attachments: [] });
      const vehicle = state.vehicles.find((item) => item.id === vehicleId);
      if (vehicle) {
        if (Number.isFinite(Number(body.mileage)) && Number(body.mileage) >= 0) vehicle.mileage = Math.round(Number(body.mileage));
        vehicle.workshopSource = "DOSSIER";
        vehicle.workshopDossierId = String(thread.dossierId);
        vehicle.workshopDossierNumber = dossierNumber;
        vehicle.dossierLinkedAt = now;
      }
      if ((overview.dossier as Record<string,unknown>).demoStage==="request_received") setVehicleStage(state, vehicleId, "Pedido recebido", 5, "A oficina irá propor uma marcação.");
      scheduleDemoReply(state, overview, "Recebemos o seu pedido. Vamos analisar os detalhes e preparar o próximo passo.", false);
      overview.nextWorkshopAt = Date.now()+demoDelay(state);
      saveDemoState(state);
      return json({ ...overview, created: true, synced: true });
    }
    if (operation === "messages" && method === "POST") {
      const body = parseBody(init);
      const messages = overview.messages as Record<string, unknown>[];
      messages.push({ id: `demo-message-${crypto.randomUUID()}`, sender: "CUSTOMER", body: String(body.body || ""), createdAt: new Date().toISOString(), attachments: [] });
      scheduleDemoReply(state, overview, String(body.body || ""));
      saveDemoState(state);
      return json({ ...overview, created: true, synced: true });
    }
    if (operation === "attachments" && method === "POST" && init.body instanceof FormData) {
      const file = init.body.get("file");
      const body = String(init.body.get("body") || "");
      if (!(file instanceof File)) return json({ error: "FILE_REQUIRED" }, 400);
      const messages = overview.messages as Record<string, unknown>[];
      messages.push({ id: `demo-message-${crypto.randomUUID()}`, sender: "CUSTOMER", body, createdAt: new Date().toISOString(), attachments: [{ id: `demo-file-${crypto.randomUUID()}`, name: file.name, mime: file.type || "application/octet-stream", size: file.size, url: await fileUrl(file) }] });
      scheduleDemoReply(state, overview, "Recebemos o seu anexo e vamos verificar.", false);
      saveDemoState(state);
      return json({ ...overview, created: true, synced: true });
    }
  }

  const dossierMatch = path.match(/^\/api\/v1\/vehicles\/([^/]+)\/dossier\/(decision|action|change-request|acceptance)$/);
  if (dossierMatch && method === "POST") {
    const vehicleId = decodeURIComponent(dossierMatch[1]);
    const operation = dossierMatch[2];
    const overview = vehicleOverview(state, vehicleId);
    if (!overview) return json({ error: "DEMO_VEHICLE_NOT_FOUND" }, 404);
    const dossier = overview.dossier as Record<string, unknown>;
    if(!dossier)return json({error:"DEMO_REQUEST_REQUIRED"},409);
    const body = parseBody(init);

    if(operation==="acceptance"){dossier.termsAcceptedAt=body.termsAcceptedAt;addTimeline(overview,"Termos aceites","Aceitação fictícia registada.");}
    if (operation === "decision") {
      const decision = String(body.decision || "contact");
      const decisionType = String(body.decisionType || "quote");
      const reference = String(body.reference || "");
      if (decisionType === "initial") {
        if ((dossier.initialDiagnostic as Record<string,unknown>).status===decision)return json({...overview,updated:true});
        if ((dossier.initialDiagnostic as Record<string,unknown>).status!=="pending")return json({error:"DECISION_NOT_PENDING"},409);
        (dossier.initialDiagnostic as Record<string, unknown>).status = decision;
        if (decision === "approved") dossier.demoStage = "diagnostic_approved";
      }
      if (decisionType === "quote") {
        const quotes = dossier.quotes as Record<string, unknown>[];
        const quote = quotes.find((item) => String(item.id || item.version) === reference);
        if (!quote)return json({error:"QUOTE_NOT_FOUND"},404);
        if (quote.status===decision)return json({...overview,updated:true});
        if (quote.status!=="pending")return json({error:"DECISION_NOT_PENDING"},409);
        quote.status = decision;
        if (decision === "approved") dossier.demoStage = "quote_approved";
      }
      if (decisionType === "diagnostic") {
        const approvals = dossier.diagnosticApprovals as Record<string, unknown>[];
        const approval = approvals.find((item) => String(item.id) === reference);
        if (approval) approval.status = decision;
      }
      const title = decision === "approved" ? "Autorização aprovada pelo cliente" : decision === "rejected" ? "Autorização recusada pelo cliente" : "Cliente pediu contacto da oficina";
      addTimeline(overview, title, decisionType === "quote" ? "Decisão relativa ao orçamento." : "Decisão relativa ao diagnóstico.");
      if (decisionType === "initial") setVehicleStage(state, vehicleId, decision === "approved" ? "Diagnóstico autorizado" : "Diagnóstico não autorizado", 12, decision === "approved" ? "A oficina pode iniciar o diagnóstico." : "A oficina irá contactar o cliente antes de avançar.");
      else if (decisionType === "quote") setVehicleStage(state, vehicleId, decision === "approved" ? "Orçamento aprovado" : "Orçamento por rever", 45, decision === "approved" ? "A oficina pode iniciar os trabalhos autorizados." : "A oficina irá rever a proposta com o cliente.");
      else updateVehicleSummary(state, vehicleId, decision === "approved" ? "Decisão recebida. A oficina irá atualizar o próximo passo." : "A oficina irá contactar o cliente antes de avançar.");
    }

    if (operation === "action") {
      const action = String(body.serviceAction || "");
      const value = String(body.value || "");
      const appointment = dossier.appointment as Record<string, unknown>;
      if (action === "appointment_confirm") {
        appointment.status = "confirmed";
        appointment.confirmedAt = new Date().toISOString();
        appointment.rentalCarRequested = value === "rental_yes";
        if (value === "rental_yes") {
          const rentalCar = appointment.rentalCar as Record<string, unknown>;
          rentalCar.requestedAt = new Date().toISOString();
          rentalCar.licenseVerified = true;
        }
        dossier.demoStage = "appointment_confirmed";
        addTimeline(overview, "Marcação confirmada pelo cliente", value === "rental_yes" ? "Carro de substituição solicitado · SEAT Ibiza ou equivalente · CHF 60/dia · 100 km/dia incluídos." : "Cliente não necessita de carro de substituição.");
        setVehicleStage(state, vehicleId, "Marcação confirmada", 8, "A oficina irá preparar o diagnóstico.");
      } else if (action === "appointment_reschedule") {
        const approvedSlots = Array.isArray(appointment.preApprovedSlots) ? appointment.preApprovedSlots.map(String) : [];
        if (!approvedSlots.includes(value.slice(0, 16))) return json({ error: "SLOT_NOT_PREAPPROVED", message: "Este horário não foi pré-aprovado pela oficina." }, 409);
        appointment.status = "proposed";
        appointment.scheduledAt = value.slice(0, 16);
        appointment.requestedScheduledAt = "";
        appointment.workshopChangeReason = "Horário pré-aprovado pela oficina e selecionado pelo cliente.";
        dossier.demoStage = "appointment_proposed";
        addTimeline(overview, "Horário pré-aprovado selecionado", value.replace("T", " "));
        updateVehicleSummary(state, vehicleId, "Confirme a nova marcação selecionada.");
      } else if (action === "appointment_cancel") {
        appointment.status = "cancelled";
        appointment.cancelledBy = "customer";
        addTimeline(overview, "Marcação anulada pelo cliente", "A oficina foi informada.");
        updateVehicleSummary(state, vehicleId, "Marcação anulada. Pode solicitar uma nova data.");
      } else if (action === "rental_cancel") {
        appointment.rentalCarRequested = false;
        addTimeline(overview, "Reserva do carro de substituição cancelada", "O cliente informou que já não necessita do veículo de substituição.");
        updateVehicleSummary(state, vehicleId, "Reserva do carro de substituição cancelada.");
      } else if (action === "after_hours_request") {
        const dropoff = dossier.dropoff as Record<string, unknown>;
        dropoff.afterHoursRequest = { status: "pending", requestedAt: new Date().toISOString() };
        addTimeline(overview, "Pedido de cofre enviado", "Cliente pretende entregar o veículo fora do horário.");
      } else if (action === "dropoff_method" || action === "dropoff_confirm") {
        const dropoff = dossier.dropoff as Record<string,unknown>;
        if(action==="dropoff_method") dropoff.method=value;
        else dropoff.customerConfirmedAt=new Date().toISOString();
        addTimeline(overview,"Entrega registada",value || "O cliente confirmou a entrega do veículo.");
      } else if (action === "payment_choice") {
        const payment = dossier.payment as Record<string, unknown>;
        if(payment.status==="paid")return json({...overview,updated:true});
        if(dossier.demoStage!=="payment_due"&&dossier.demoStage!=="payment_selected")return json({error:"PAYMENT_NOT_DUE"},409);
        if(value==="account_card"&&state.wallet.balance<Number(payment.amount))return json({error:"DEMO_INSUFFICIENT_FUNDS",message:"Saldo fictício insuficiente. Adicione saldo na Carteira."},409);
        if(value==="account_card"&&!state.wallet.cardEnabled)return json({error:"DEMO_CARD_DISABLED",message:"Ative o cartão fictício na Carteira."},409);
        payment.customerChoice = { method: value, selectedAt: new Date().toISOString() };
        const paymentLabels: Record<string, string> = { account_card: "Cartão associado •••• 4242", paypal: "PayPal", google_pay: "Google Pay", apple_pay: "Apple Pay", twint: "TWINT", local: "Pagamento na oficina" };
        const digital = ["account_card", "paypal", "google_pay", "apple_pay", "twint"].includes(value);
        addTimeline(overview, digital ? "Pagamento digital confirmado" : "Método de pagamento escolhido", paymentLabels[value] || value);
        if (digital) {
          payment.status = "paid";
          recordDemoOperation(state,vehicleId,overview,"payment_confirmed");
          payment.paidAt = new Date().toISOString();
          const pickup = dossier.pickup as Record<string, unknown>;
          pickup.authorizedAt = new Date().toISOString();
      (pickup.locker as Record<string,unknown>).code ||= String(1000+Math.floor(Math.random()*9000));
          dossier.demoStage = "payment_confirmed";
          addWorkshopMessage(overview, "Pagamento confirmado na aplicação. Escolha agora como pretende levantar o veículo.");
          setVehicleStage(state, vehicleId, "Pronto para levantamento", 100, "Pagamento confirmado. Escolha a forma de levantamento.");
        } else {
          dossier.demoStage = "payment_selected";
          updateVehicleSummary(state, vehicleId, "Pagamento será efetuado localmente na oficina.");
        }
      } else if (action === "pickup_method") {
        const pickup = dossier.pickup as Record<string, unknown>;
        if((dossier.payment as Record<string,unknown>).status!=="paid")return json({error:"PAYMENT_REQUIRED"},409);
        pickup.method = value;
        dossier.demoStage = "pickup_selected";
        addTimeline(overview, "Forma de levantamento escolhida", value === "locker" ? "Cofre fora do horário" : "Durante o horário da oficina");
        updateVehicleSummary(state, vehicleId, "Forma de levantamento registada.");
      } else if (action === "availability_update") {
        let slots: unknown = [];
        try { slots = JSON.parse(value); } catch { slots = []; }
        if (!Array.isArray(slots) || slots.some((slot) => typeof slot !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(slot))) return json({ error: "INVALID_AVAILABILITY" }, 400);
        appointment.preApprovedSlots = Array.from(new Set(slots.map(String))).sort();
        addTimeline(overview, "Disponibilidade atualizada pela oficina", `${(appointment.preApprovedSlots as string[]).length} horários pré-aprovados.`);
      } else if (action === "delivery_estimate_update") {
        if (value.trim().length < 3) return json({ error: "DELIVERY_ESTIMATE_REQUIRED" }, 400);
        dossier.deliveryEstimate = value.trim();
        addTimeline(overview, "Estimativa de entrega atualizada", value.trim());
        addWorkshopMessage(overview, `Atualizámos a previsão de entrega: ${value.trim()}`);
        updateVehicleSummary(state, vehicleId, value.trim());
      }
    }

    if (operation === "change-request") {
      const requests = dossier.changeRequests as Record<string, unknown>[];
      requests.push({ id: `demo-change-${crypto.randomUUID()}`, kind: body.kind || "repair", reference: body.reference || "", body: body.body || "", status: "pending", createdAt: new Date().toISOString() });
      addTimeline(overview, "Pedido de alteração enviado", String(body.body || ""));
      updateVehicleSummary(state, vehicleId, "Pedido de alteração em análise pela oficina.");
    }

    overview.nextWorkshopAt=Date.now()+demoDelay(state);
    saveDemoState(state);
    return json({ ...overview, updated: true });
  }

  const extra = await demoExtras(state,path,method,init.body instanceof FormData ? Object.fromEntries(init.body.entries()) : parseBody(init),demoPdf);
  if(extra){saveDemoState(state);return extra;}

  if (path === "/api/v1/security/sessions" && method === "GET") return json({ sessions: [{ id: "demo-session", createdAt: shiftedIso(-1), expiresAt: shiftedIso(30), lastSeenAt: new Date().toISOString(), userAgent: "Precision EV Demo", current: true }] });
  if (path === "/api/v1/security" && method === "GET") return json({ twoFactorEnabled: false, twoFactorEnabledAt: null, recoveryCodesRemaining: 0, suspiciousAlertsEnabled: true, passwordChangedAt: shiftedIso(-30), events: [] });
  if (path === "/api/v1/privacy/exports" && method === "GET") return json({ requests: [] });

  return json({ error:"DEMO_ACTION_UNAVAILABLE", message:"Esta ação ainda não está simulada; nenhum pedido foi enviado ao serviço real." },501);
}
